/**
 * Nexus Embed Interactive Suggestions
 *
 * Renders schedule grids and forms with Confirm. On confirm, sends
 * a single user-message string so the LLM interprets it like a
 * suggested response. Forms use standard autocomplete for browser autofill.
 *
 * Primary responsibilities:
 * - ScheduleGridComponent: grid of slot buttons + Confirm
 * - FormComponent: labeled inputs with autocomplete + Confirm
 */

'use client'

import React, { useState } from 'react'
import type {
  ScheduleGridSuggestion,
  ScheduleCalendarSuggestion,
  ScheduleTimeSuggestion,
  FormSuggestion,
  FormFieldSpec
} from '@/types/nexus-interactive-suggestions'

const SUGGESTION_PANEL_CLASS =
  'mt-2 w-[min(92%,24rem)] rounded-xl border border-gray-200/70 bg-white/95 p-3 shadow-[0_16px_44px_-34px_rgba(15,23,42,0.55)] backdrop-blur-sm'

const SUGGESTION_TITLE_CLASS = 'mb-2 text-xs font-medium text-gray-600'

const SUGGESTION_OPTION_CLASS =
  'rounded-lg border px-3 py-2 text-left text-sm transition-[transform,border-color,background-color,color,box-shadow] hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] disabled:opacity-50'

const SUGGESTION_CONFIRM_CLASS =
  'mt-3 w-full rounded-lg border px-4 py-2.5 text-sm font-medium transition-[transform,border-color,background-color,color] active:scale-[0.99] disabled:opacity-50'

function getBrandColor(accent: string, gradient?: string): string {
  return gradient ? gradient.split(',')[0]?.trim() || accent : accent
}

/** Map field id/type to HTML autocomplete for browser autofill. */
function getAutocomplete(field: FormFieldSpec): string | undefined {
  if (field.autocomplete) return field.autocomplete
  const id = field.id.toLowerCase()
  if (id.includes('name') || id === 'fullname' || id === 'full_name') return 'name'
  if (id.includes('email')) return 'email'
  if (id.includes('phone') || id.includes('tel') || id.includes('number')) return 'tel'
  if (id.includes('address')) return 'street-address'
  if (field.type === 'email') return 'email'
  if (field.type === 'tel') return 'tel'
  return undefined
}

export interface ScheduleGridComponentProps {
  suggestion: ScheduleGridSuggestion
  accent: string
  gradient?: string
  backgroundStyle?: string
  onConfirm: (payload: string) => void
  disabled?: boolean
}

export function ScheduleGridComponent({
  suggestion,
  accent,
  gradient,
  backgroundStyle,
  onConfirm,
  disabled
}: ScheduleGridComponentProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // Brand color for buttons (first gradient color or accent)
  const brandColor = getBrandColor(accent, gradient)

  const handleConfirm = () => {
    if (!selectedId || disabled) return
    const slot = (suggestion.slots || []).find((s) => s.id === selectedId)
    const payload = slot ? `I'd like to book: ${slot.label}` : `Selected: ${selectedId}`
    onConfirm(payload)
  }

  const slots = Array.isArray(suggestion.slots) ? suggestion.slots : []

  // Platform button radius (16px = rounded-2xl)
  return (
    <div className={SUGGESTION_PANEL_CLASS}>
      {suggestion.title && <p className={SUGGESTION_TITLE_CLASS}>{suggestion.title}</p>}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {slots.map((slot) => (
          <button
            key={slot.id}
            type="button"
            disabled={disabled}
            onClick={() => setSelectedId(selectedId === slot.id ? null : slot.id)}
            className={`${SUGGESTION_OPTION_CLASS} ${
              selectedId === slot.id
                ? 'border-current opacity-90 shadow-inner'
                : 'border-gray-200/80 hover:border-gray-300'
            }`}
            style={
              selectedId === slot.id
                ? { borderColor: brandColor, color: brandColor, background: `${brandColor}15` }
                : { color: '#374151' }
            }
          >
            {slot.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        disabled={disabled || !selectedId}
        onClick={handleConfirm}
        className={SUGGESTION_CONFIRM_CLASS}
        style={{
          borderColor: brandColor,
          color: selectedId && !disabled ? brandColor : '#9CA3AF',
          background: selectedId && !disabled ? `${brandColor}18` : 'transparent'
        }}
      >
        Confirm
      </button>
    </div>
  )
}

export interface ScheduleCalendarComponentProps {
  suggestion: ScheduleCalendarSuggestion
  accent: string
  gradient?: string
  onConfirm: (payload: string) => void
  disabled?: boolean
}

/** Build list of days in month for calendar. */
function getDaysInMonth(
  year: number,
  month: number
): { date: string; day: number; isCurrentMonth: boolean }[] {
  const first = new Date(year, month, 1)
  const last = new Date(year, month + 1, 0)
  const startPad = first.getDay()
  const days: { date: string; day: number; isCurrentMonth: boolean }[] = []
  const pad = new Date(first)
  pad.setDate(pad.getDate() - startPad)
  for (let i = 0; i < startPad; i++) {
    days.push({
      date: pad.toISOString().slice(0, 10),
      day: pad.getDate(),
      isCurrentMonth: false
    })
    pad.setDate(pad.getDate() + 1)
  }
  for (let d = 1; d <= last.getDate(); d++) {
    days.push({
      date: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
      day: d,
      isCurrentMonth: true
    })
  }
  const remaining = 42 - days.length
  const nextMonthStart = new Date(year, month + 1, 1)
  for (let i = 0; i < remaining; i++) {
    const d = new Date(nextMonthStart)
    d.setDate(d.getDate() + i)
    days.push({
      date: d.toISOString().slice(0, 10),
      day: d.getDate(),
      isCurrentMonth: false
    })
  }
  return days
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function ScheduleCalendarComponent({
  suggestion,
  accent,
  gradient,
  onConfirm,
  disabled
}: ScheduleCalendarComponentProps) {
  const brandColor = getBrandColor(accent, gradient)
  const now = new Date()
  const [year, setYear] = useState(
    suggestion.startDate ? parseInt(suggestion.startDate.slice(0, 4), 10) : now.getFullYear()
  )
  const [month, setMonth] = useState(
    suggestion.startDate ? parseInt(suggestion.startDate.slice(5, 7), 10) - 1 : now.getMonth()
  )
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  const days = getDaysInMonth(year, month)
  const unavailableSet = new Set(suggestion.unavailableDates ?? [])
  const todayYmd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`

  const handleConfirm = () => {
    if (!selectedDate || disabled) return
    onConfirm(`I'd like to book: ${selectedDate}`)
  }

  const prevMonth = () => {
    if (month === 0) {
      setMonth(11)
      setYear((y) => y - 1)
    } else setMonth((m) => m - 1)
  }
  const nextMonth = () => {
    if (month === 11) {
      setMonth(0)
      setYear((y) => y + 1)
    } else setMonth((m) => m + 1)
  }

  return (
    <div className={SUGGESTION_PANEL_CLASS}>
      {suggestion.title && <p className={SUGGESTION_TITLE_CLASS}>{suggestion.title}</p>}
      <div className="flex items-center justify-between gap-2 pb-2">
        <button
          type="button"
          disabled={disabled}
          onClick={prevMonth}
          className="rounded-lg px-2 py-1 text-sm font-medium transition-[transform,background-color] hover:bg-gray-100 active:scale-95 disabled:opacity-50"
          style={{ color: brandColor }}
        >
          ←
        </button>
        <span className="text-sm font-medium text-gray-700">
          {new Date(year, month).toLocaleString('default', { month: 'long', year: 'numeric' })}
        </span>
        <button
          type="button"
          disabled={disabled}
          onClick={nextMonth}
          className="rounded-lg px-2 py-1 text-sm font-medium transition-[transform,background-color] hover:bg-gray-100 active:scale-95 disabled:opacity-50"
          style={{ color: brandColor }}
        >
          →
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs">
        {WEEKDAYS.map((w) => (
          <div key={w} className="py-1 font-medium text-gray-500">
            {w}
          </div>
        ))}
        {days.map(({ date, day, isCurrentMonth }) => {
          const isUnavailable = unavailableSet.has(date)
          const isToday = date === todayYmd
          return (
            <button
              key={date}
              type="button"
              disabled={disabled || isUnavailable}
              onClick={() => !isUnavailable && setSelectedDate(selectedDate === date ? null : date)}
              className={`rounded-lg py-1.5 text-sm transition-[transform,background-color,color] active:scale-95 disabled:opacity-50 ${
                selectedDate === date
                  ? 'font-semibold'
                  : isUnavailable
                    ? 'cursor-not-allowed text-gray-400 line-through'
                    : isCurrentMonth
                      ? 'text-gray-800 hover:bg-gray-100'
                      : 'text-gray-400'
              } ${isToday && !selectedDate ? 'ring-2 ring-offset-1' : ''}`}
              style={
                selectedDate === date
                  ? { background: `${brandColor}20`, color: brandColor }
                  : isToday && !selectedDate
                    ? { borderColor: brandColor, borderWidth: 2, boxSizing: 'border-box' }
                    : undefined
              }
              title={isUnavailable ? 'Not available' : isToday ? 'Today' : undefined}
            >
              {day}
              {isToday && isCurrentMonth && <span className="sr-only"> (Today)</span>}
            </button>
          )
        })}
      </div>
      <button
        type="button"
        disabled={disabled || !selectedDate}
        onClick={handleConfirm}
        className={SUGGESTION_CONFIRM_CLASS}
        style={{
          borderColor: brandColor,
          color: selectedDate && !disabled ? brandColor : '#9CA3AF',
          background: selectedDate && !disabled ? `${brandColor}18` : 'transparent'
        }}
      >
        Confirm
      </button>
    </div>
  )
}

export interface ScheduleTimeComponentProps {
  suggestion: ScheduleTimeSuggestion
  accent: string
  gradient?: string
  backgroundStyle?: string
  onConfirm: (payload: string) => void
  disabled?: boolean
}

export function ScheduleTimeComponent({
  suggestion,
  accent,
  gradient,
  backgroundStyle,
  onConfirm,
  disabled
}: ScheduleTimeComponentProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const brandColor = getBrandColor(accent, gradient)

  const handleConfirm = () => {
    if (!selectedId || disabled) return
    const slot = suggestion.slots?.find((s) => s.id === selectedId)
    const payload = slot ? `I'd like to book: ${slot.label}` : `Selected: ${selectedId}`
    onConfirm(payload)
  }

  const slots = suggestion.slots?.length ? suggestion.slots : []

  return (
    <div className={SUGGESTION_PANEL_CLASS}>
      {suggestion.title && (
        <div className="mb-3 flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          <p className="text-sm font-semibold text-gray-800">{suggestion.title}</p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {slots.map((slot) => (
          <button
            key={slot.id}
            type="button"
            disabled={disabled}
            onClick={() => setSelectedId(selectedId === slot.id ? null : slot.id)}
            className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2.5 text-sm font-medium transition-all hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] disabled:opacity-50 ${
              selectedId === slot.id
                ? 'border-current shadow-sm'
                : 'border-gray-200/80 hover:border-gray-300'
            }`}
            style={
              selectedId === slot.id
                ? { borderColor: brandColor, color: brandColor, background: `${brandColor}12` }
                : { color: '#374151' }
            }
          >
            {selectedId === slot.id && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            )}
            {slot.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        disabled={disabled || !selectedId}
        onClick={handleConfirm}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-[transform,border-color,background-color,color] active:scale-[0.99] disabled:opacity-50"
        style={{
          borderColor: brandColor,
          color: selectedId && !disabled ? '#fff' : '#9CA3AF',
          background: selectedId && !disabled ? brandColor : 'transparent'
        }}
      >
        {selectedId && (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
        Confirm
      </button>
    </div>
  )
}

export interface FormComponentProps {
  suggestion: FormSuggestion
  accent: string
  gradient?: string
  onConfirm: (payload: string) => void
  disabled?: boolean
}

export function FormComponent({
  suggestion,
  accent,
  gradient,
  onConfirm,
  disabled
}: FormComponentProps) {
  const fields = Array.isArray(suggestion.fields) ? suggestion.fields : []
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.id, f.prefill || '']))
  )

  const handleChange = (id: string, value: string) => {
    setValues((prev) => ({ ...prev, [id]: value }))
  }

  const handleConfirm = () => {
    if (disabled) return
    const parts = fields
      .filter((f) => values[f.id]?.trim())
      .map((f) => `${f.label}: ${values[f.id].trim()}`)
    const payload = parts.length > 0 ? parts.join('\n') : ''
    if (payload) onConfirm(payload)
  }

  const brandColor = getBrandColor(accent, gradient)
  const allRequiredFilled = fields.every(
    (f) => !f.required || (values[f.id]?.trim() ?? '').length > 0
  )
  const hasAnyValue = fields.some((f) => (values[f.id]?.trim() ?? '').length > 0)
  const canSubmit = allRequiredFilled && hasAnyValue

  return (
    <div className={SUGGESTION_PANEL_CLASS}>
      {suggestion.title && (
        <div className="mb-3 flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <p className="text-sm font-semibold text-gray-800">{suggestion.title}</p>
        </div>
      )}
      <div className="flex flex-col gap-3">
        {fields.map((field) => {
          const inputType =
            field.type === 'textarea' ? 'text' : field.type === 'number' ? 'number' : field.type
          const icon = getFieldIcon(field)
          return (
            <div key={field.id} className="flex flex-col gap-1">
              <label
                htmlFor={`embed-form-${field.id}`}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-600"
              >
                {icon}
                {field.label}
                {field.required && <span className="text-red-500">*</span>}
              </label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              {field.type === 'textarea' ? (
                <textarea
                  id={`embed-form-${field.id}`}
                  name={field.id}
                  placeholder={field.placeholder || getFieldPlaceholder(field)}
                  required={field.required}
                  value={values[field.id] ?? ''}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                  disabled={disabled}
                  rows={3}
                  className="rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2.5 pl-9 text-sm outline-none transition-colors placeholder:text-gray-400 focus:border-gray-300 focus:bg-white focus:ring-1 focus:ring-gray-300"
                />
              ) : (
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {getFieldInputIcon(field)}
                  </span>
                  <input
                    id={`embed-form-${field.id}`}
                    name={field.id}
                    type={inputType}
                    placeholder={field.placeholder || getFieldPlaceholder(field)}
                    autoComplete={getAutocomplete(field) ?? undefined}
                    required={field.required}
                    value={values[field.id] ?? ''}
                    onChange={(e) => handleChange(field.id, e.target.value)}
                    disabled={disabled}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2.5 pl-9 text-sm outline-none transition-colors placeholder:text-gray-400 focus:border-gray-300 focus:bg-white focus:ring-1 focus:ring-gray-300"
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>
      <button
        type="button"
        disabled={disabled || !canSubmit}
        onClick={handleConfirm}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-[transform,border-color,background-color,color] active:scale-[0.99] disabled:opacity-50"
        style={{
          borderColor: brandColor,
          color: canSubmit && !disabled ? '#fff' : '#9CA3AF',
          background: canSubmit && !disabled ? brandColor : 'transparent'
        }}
      >
        {canSubmit && (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
        Confirm
      </button>
    </div>
  )
}

/** Icon helper: SVG icons for form field labels */
function getFieldIcon(field: FormFieldSpec): React.ReactNode {
  const id = field.id.toLowerCase()
  if (id.includes('name') || id === 'full_name' || id === 'fullname') {
    return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
  }
  if (id.includes('email') || field.type === 'email') {
    return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
  }
  if (id.includes('phone') || id.includes('tel') || field.type === 'tel') {
    return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
  }
  if (id.includes('company') || id.includes('business')) {
    return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/></svg>
  }
  return null
}

/** Icon helper: SVG icons inside input fields */
function getFieldInputIcon(field: FormFieldSpec): React.ReactNode {
  const id = field.id.toLowerCase()
  if (id.includes('name') || id === 'full_name' || id === 'fullname') {
    return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
  }
  if (id.includes('email') || field.type === 'email') {
    return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
  }
  if (id.includes('phone') || id.includes('tel') || field.type === 'tel') {
    return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
  }
  if (id.includes('company') || id.includes('business')) {
    return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/></svg>
  }
  // Default: a generic form icon
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>
}

/** Placeholder helper */
function getFieldPlaceholder(field: FormFieldSpec): string {
  const id = field.id.toLowerCase()
  if (id.includes('name')) return 'Enter your name'
  if (id.includes('email')) return 'you@example.com'
  if (id.includes('phone') || id.includes('tel')) return '+1 (555) 000-0000'
  if (id.includes('company')) return 'Company name'
  return `Enter ${field.label.toLowerCase()}`
}
